import React, { useState, useRef, useEffect } from 'react';

export const Panel: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className }) => (
    <div className={`glass-panel rounded-2xl shadow-xl ${className}`}>
        {children}
    </div>
);

export const Header: React.FC<{ title: string, children?: React.ReactNode }> = ({ title, children }) => (
    <header className="flex justify-between items-center mb-6">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-200 tracking-tight">{title}</h2>
        {children}
    </header>
);

export const EditableField: React.FC<{ value: string, onSave: (newValue: string) => void, className?: string, inputClassName?: string, placeholder?: string }> = ({ value, onSave, className, inputClassName, placeholder }) => {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { setText(value) }, [value]);
    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

    const handleSave = () => {
        setEditing(false);
        if (text !== value) {
            onSave(text);
        }
    };

    return editing ? (
        <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            onBlur={handleSave}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            className={inputClassName}
            placeholder={placeholder}
        />
    ) : (
        <div onClick={() => setEditing(true)} className={className}>
            {text || <span className="text-white/80">{placeholder}</span>}
        </div>
    );
};

export const Tooltip: React.FC<{ text: string, children: React.ReactElement }> = ({ text, children }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="relative flex items-center">
            {React.cloneElement(children as React.ReactElement<any>, {
                onMouseEnter: () => setShow(true),
                onMouseLeave: () => setShow(false)
            })}
            {show && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded-md shadow-lg z-10">
                    {text}
                </div>
            )}
        </div>
    );
};

export const DraggableSidebarItem: React.FC<{ icon: React.ReactElement, label: string, onDragStart: (e: React.DragEvent) => void, onClick?: () => void, onContextMenu?: (e: React.MouseEvent) => void }> = ({ icon, label, onDragStart, onClick, onContextMenu }) => (
    <div
        draggable
        onDragStart={onDragStart}
        onClick={onClick}
        onContextMenu={onContextMenu}

        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-neon-gold transition-colors group ${onClick ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
    >
        {React.cloneElement(icon as React.ReactElement<any>, { className: 'w-5 h-5 flex-shrink-0 text-gray-500 group-hover:text-neon-gold transition-colors' })}
        <span className="truncate flex-1 text-left">{label}</span>
    </div>
);

export const SidebarSection: React.FC<{ title: string, children: React.ReactNode }> = ({ title, children }) => (
    <div className="py-2">
        <h3 className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{title}</h3>
        <div className="space-y-1 px-2">
            {children}
        </div>
    </div>
);

export const SidebarButton: React.FC<{
    icon: React.ReactElement<{ className?: string }>,
    label: string,
    active?: boolean,
    onClick: () => void,
    isDanger?: boolean,
    isDropping?: boolean,
    onDragOver?: React.DragEventHandler<HTMLButtonElement>,
    onDragEnter?: React.DragEventHandler<HTMLButtonElement>,
    onDragLeave?: React.DragEventHandler<HTMLButtonElement>,
    onDrop?: React.DragEventHandler<HTMLButtonElement>,
    onContextMenu?: React.MouseEventHandler<HTMLButtonElement>,
}> = ({ icon, label, active, onClick, isDanger, isDropping, onDragOver, onDragEnter, onDragLeave, onDrop, onContextMenu }) => (
    <button onClick={onClick}
        onContextMenu={onContextMenu}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border ${isDropping
            ? 'bg-neon-gold/20 border-neon-gold/50 text-white'
            : active
                ? 'bg-white/10 text-neon-gold border-neon-gold/20 shadow-[0_0_15px_rgba(245,158,11,0.1)]'
                : isDanger
                    ? 'text-red-400 hover:bg-red-500/10 border-transparent'
                    : 'text-white/80 hover:bg-white/5 hover:text-white border-transparent hover:border-white/10'
            }`}>
        {React.cloneElement(icon as React.ReactElement<any>, { className: 'w-5 h-5 flex-shrink-0 text-current' })}
        <span className="truncate flex-1 text-left">{label}</span>
    </button>
);
